import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthSession } from '../api/client';
import { getSupportNotificationPrefs, updateSupportNotificationPrefs, type SupportNotificationPrefs } from '../api/support';

export default function SupportNotificationSettings() {
  const { t } = useTranslation('chats');
  const [prefs, setPrefs] = useState<SupportNotificationPrefs | null>(null);
  const [prefsBusy, setPrefsBusy] = useState(true);
  const [prefsError, setPrefsError] = useState('');
  const savingPrefs = useRef(false);
  const alive = useRef(false);
  const session = useRef(getAuthSession());
  const valid = useCallback(() => alive.current && session.current === getAuthSession(), []);

  const loadPrefs = useCallback(async () => {
    setPrefsBusy(true);
    setPrefsError('');
    try {
      const result = await getSupportNotificationPrefs();
      if (valid()) setPrefs(result);
    } catch {
      if (valid()) setPrefsError(t('supportPrefsLoadFailed'));
    } finally {
      if (valid()) setPrefsBusy(false);
    }
  }, [t, valid]);

  async function togglePref(key: keyof SupportNotificationPrefs) {
    if (!prefs || savingPrefs.current) return;
    savingPrefs.current = true;
    setPrefsBusy(true);
    setPrefsError('');
    try {
      const result = await updateSupportNotificationPrefs({ ...prefs, [key]: !prefs[key] });
      if (valid()) setPrefs(result);
    } catch {
      if (valid()) setPrefsError(t('supportPrefsSaveFailed'));
    } finally {
      savingPrefs.current = false;
      if (valid()) setPrefsBusy(false);
    }
  }

  useEffect(() => {
    alive.current = true;
    void loadPrefs();
    return () => { alive.current = false; };
  }, [loadPrefs]);

  return (
    <section className="support-notification-settings" aria-label={t('supportNotifications')}>
      <div className="support-notification-title">{t('supportNotifications')}</div>
      <p className="support-notification-hint">{t('supportNotificationsShared')}</p>
      <label className="support-notification-row">
        <span>{t('supportNotifyErrors')}</span>
        <span className="switch">
          <input
            type="checkbox"
            role="switch"
            checked={prefs?.notifyErrors ?? true}
            disabled={!prefs || prefsBusy}
            onChange={() => void togglePref('notifyErrors')}
          />
          <span className="switch-track"><span className="switch-thumb" /></span>
        </span>
      </label>
      <label className="support-notification-row">
        <span>{t('supportNotifyUsers')}</span>
        <span className="switch">
          <input
            type="checkbox"
            role="switch"
            checked={prefs?.notifyUsers ?? true}
            disabled={!prefs || prefsBusy}
            onChange={() => void togglePref('notifyUsers')}
          />
          <span className="switch-track"><span className="switch-thumb" /></span>
        </span>
      </label>
      {prefsError && <p role="alert" className="error">{prefsError}</p>}
      {!prefs && !prefsBusy && prefsError && <button type="button" className="btn btn-ghost" onClick={() => void loadPrefs()}>{t('supportRetry')}</button>}
    </section>
  );
}
